import { storeToRefs } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import fetcher from '@/fetcher';
import useStateStore from '@/stores/state';
import {
  computeProgressPct,
  computeVersionState,
  displayedVersionFor,
  estimateDownloadMinutes,
  formatDuration,
  formatRemainingTimeMinutes,
  versionLabel,
} from '@/utils/sidebar_status';

function sidebarStatus() {
  const { t } = useI18n();

  const stateStore = useStateStore();
  const { state, wiringDisplayRows, wiringDisplayMode, wiringHasUiOverrides, wiringValues, transfersUi } =
    storeToRefs(stateStore);

  const nateState = computed(() => state.value?.data?.nate || {});
  const channelsProd = computed(() => state.value?.data?.channels?.production || {});
  const devChannel = computed(() => state.value?.data?.channels?.development?.dev || {});
  const transfers = computed(() => transfersUi.value || {});
  const downlinkBps = computed(() => Number(state.value?.data?.wan?.speedtest?.downlink_bps || 0));

  const currentVersionLabel = computed(() => {
    const label = versionLabel(nateState.value?.image || nateState.value);
    return label !== '-' ? label : null;
  });
  const nateStatus = computed(() => nateState.value?.status || '');

  const nateStatusUi = computed(() => {
    if (nateStatus.value === 'running') return { color: 'green', label: t('sidebar_status.nate_running') };
    if (nateStatus.value === 'restarting') return { color: 'red', label: t('sidebar_status.nate_restarting') };
    return { color: 'red', label: t('sidebar_status.nate_not_running') };
  });

  const nateUptimeText = computed(() =>
    nateState.value?.uptime_s != null ? formatDuration(nateState.value.uptime_s) : null,
  );
  const nateStartedAtText = computed(() => {
    const raw = nateState.value?.started_at;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d)) return String(raw);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  });

  function makeChannelVersion(getLocal, getRemote, getTransfer) {
    const local = computed(getLocal);
    const remote = computed(getRemote);
    const transfer = computed(getTransfer);
    const versionState = computed(() =>
      computeVersionState({ local: local.value, remote: remote.value, transfer: transfer.value }),
    );
    const displayedVersion = computed(() => displayedVersionFor(versionState.value, local.value, remote.value));
    const progressPct = computed(() => computeProgressPct(transfer.value));
    const remainingTimeText = computed(() => formatRemainingTimeMinutes(t, transfer.value?.remaining_time_s));
    const estimatedMinutes = computed(() => estimateDownloadMinutes(remote.value?.size_bytes, downlinkBps.value));

    return reactive({
      local,
      remote,
      transfer,
      versionState,
      displayedVersion,
      progressPct,
      remainingTimeText,
      estimatedMinutes,
    });
  }

  const stable = makeChannelVersion(
    () => channelsProd.value?.stable?.local || null,
    () => channelsProd.value?.stable?.remote || null,
    () => transfers.value?.stable || null,
  );

  const beta = makeChannelVersion(
    () => channelsProd.value?.beta?.local || null,
    () => channelsProd.value?.beta?.remote || null,
    () => transfers.value?.beta || null,
  );

  const dev = makeChannelVersion(
    () => devChannel.value?.local || null,
    () => devChannel.value?.remote || null,
    () => {
      const tag = String(devChannel.value?.remote?.tag || '');
      return tag ? transfers.value?.[tag] || null : null;
    },
  );

  const devVisible = computed(() => Boolean(devChannel.value?.remote?.tag));

  const isActionableState = (s) => s !== 'up_to_date' && s !== 'no_version';

  const showVersions = computed(() => {
    if (isActionableState(stable.versionState)) return true;
    if (isActionableState(beta.versionState)) return true;
    return devVisible.value && isActionableState(dev.versionState);
  });

  const download = async (tag) => {
    if (!tag) return;
    await fetcher.post('/images/-/download', { tag, use_dev_registry: false });
  };

  const run = async (tag) => {
    if (wiringHasUiOverrides.value) {
      const ok = window.confirm(t('launch_tiles.run_changed_wiring_confirm'));
      if (!ok) return;
    }
    await fetcher.post('/nate/-/run', { tag, use_dev_registry: false, wiring_values: wiringValues.value });
  };

  const refreshWanStatus = async () => {
    const ok = window.confirm(t('sidebar_status.speedtest_confirm'));
    if (!ok) return;

    stateStore.markSpeedtestRequested();
    await fetcher.post('/wan/-/speedtest', null);
  };

  const showLogs = ref(false);
  const logsKind = ref('runner');

  const openLogs = ({ kind } = {}) => {
    logsKind.value = kind || 'runner';
    showLogs.value = true;
  };

  const openNateLogs = () => openLogs({ kind: 'nate' });

  return {
    currentVersionLabel,
    nateStatusUi,
    nateUptimeText,
    nateStartedAtText,

    wiringDisplayRows,
    wiringDisplayMode,

    stable,
    beta,
    dev,
    devVisible,
    showVersions,

    download,
    run,
    refreshWanStatus,

    showLogs,
    logsKind,
    openLogs,
    openNateLogs,
  };
}

export default sidebarStatus;
